import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { UserPlus, Search, X } from "lucide-react";

const digits = (v) => (v || "").replace(/\D/g, "");

// Proveedor registrado (buscador) o proveedor ocasional escrito a mano.
export function SupplierField({ kind, suppliers, value, onChange }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const list = useMemo(() => {
    const base = suppliers.filter((s) => s.supplier_type !== "foreign" &&
      (kind === "informal" ? (s.supplier_type === "informal" || digits(s.rnc_cedula).length === 11) : digits(s.rnc_cedula).length >= 9));
    const t = q.trim().toLowerCase();
    return (t ? base.filter((s) => `${s.name} ${s.rnc_cedula || ""}`.toLowerCase().includes(t)) : base).slice(0, 30);
  }, [suppliers, kind, q]);

  if (value.mode === "occasional") return <OccasionalSupplier kind={kind} value={value} onChange={onChange} />;

  return (
    <div className="relative">
      {value.supplier ? (
        <div className="mt-1 flex items-center justify-between h-9 rounded-md border border-input px-3 text-sm" data-testid="purchase-supplier-selected">
          <span className="truncate">{value.supplier.name}{value.supplier.rnc_cedula ? ` · ${value.supplier.rnc_cedula}` : ""}</span>
          <button type="button" onClick={() => onChange({ ...value, supplier: null })} data-testid="purchase-supplier-clear"><X className="w-3.5 h-3.5" /></button>
        </div>
      ) : (
        <div className="relative mt-1">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input className="pl-8" placeholder="Buscar por nombre o RNC" value={q} onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)} onChange={(e) => setQ(e.target.value)} data-testid="purchase-supplier-search" />
        </div>
      )}
      {open && !value.supplier && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-md" data-testid="purchase-supplier-list">
          {list.map((s) => (
            <button key={s.id} type="button" className="block w-full text-left px-3 py-2 text-sm hover:bg-accent"
              onMouseDown={() => { onChange({ ...value, supplier: s }); setQ(""); }} data-testid={`purchase-supplier-opt-${s.id}`}>
              {s.name} <span className="text-xs text-muted-foreground font-mono">{s.rnc_cedula}</span>
            </button>
          ))}
          {list.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Sin resultados</div>}
        </div>
      )}
      <button type="button" className="mt-1.5 text-xs text-primary font-semibold inline-flex items-center gap-1"
        onClick={() => onChange({ ...value, mode: "occasional", supplier: null })} data-testid="purchase-supplier-occasional-btn">
        <UserPlus className="w-3.5 h-3.5" /> Proveedor ocasional (escribir datos)
      </button>
    </div>
  );
}

const ID_TYPES = [
  { value: "cedula", label: "Cédula", ph: "001-0000000-0 (11 dígitos)" },
  { value: "rnc", label: "RNC", ph: "1-01-00000-0 (9 dígitos)" },
];

function OccasionalSupplier({ kind, value, onChange }) {
  const occ = value.occ;
  const set = (patch) => onChange({ ...value, occ: { ...occ, ...patch } });
  const idType = kind === "informal" ? "cedula" : occ.id_type || "rnc";
  const types = kind === "informal" ? ID_TYPES.filter((t) => t.value === "cedula") : ID_TYPES;
  const check = usePadron(idType, occ.rnc);
  useEffect(() => {
    if (kind === "formal" && check.reg && !occ.name) set({ name: check.reg.razon_social });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check.reg]);
  return (
    <div className="mt-1 rounded-md border border-dashed border-primary/50 p-3 space-y-2" data-testid="purchase-occasional-supplier">
      <Input placeholder="Nombre completo" value={occ.name} onChange={(e) => set({ name: e.target.value })} data-testid="purchase-occ-name" />
      <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr_1fr] gap-2">
        <div className="flex rounded-md border border-input overflow-hidden" data-testid="purchase-occ-id-type">
          {types.map((t) => (
            <button key={t.value} type="button" onClick={() => set({ id_type: t.value })} data-testid={`purchase-occ-id-${t.value}`}
              className={`px-2.5 text-xs font-semibold h-9 ${idType === t.value ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{t.label}</button>
          ))}
        </div>
        <Input className="font-mono" placeholder={ID_TYPES.find((t) => t.value === idType).ph} value={occ.rnc}
          onChange={(e) => set({ rnc: e.target.value })} data-testid="purchase-occ-rnc" />
        <Input placeholder="Teléfono" value={occ.phone} onChange={(e) => set({ phone: e.target.value })} data-testid="purchase-occ-phone" />
      </div>
      <IdNotice kind={kind} idType={idType} check={check} />
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2 text-xs font-normal">
          <Checkbox checked={occ.save} onCheckedChange={(v) => set({ save: !!v })} data-testid="purchase-occ-save" /> Guardar en proveedores
        </Label>
        <button type="button" className="text-xs text-muted-foreground underline" onClick={() => onChange({ ...value, mode: "registered" })}
          data-testid="purchase-occ-cancel">Elegir uno registrado</button>
      </div>
    </div>
  );
}

// Consulta el padrón DGII cuando el número tiene 9 u 11 dígitos.
function usePadron(idType, ident) {
  const [state, setState] = useState({ status: "idle", reg: null });
  useEffect(() => {
    const d = digits(ident);
    if (![9, 11].includes(d.length)) { setState({ status: d ? "incomplete" : "idle", reg: null }); return; }
    const t = setTimeout(() => {
      api.get(`/dgii/rnc/lookup/${d}`).then(({ data }) => setState({ status: "found", reg: data }))
        .catch(() => setState({ status: "notfound", reg: null }));
    }, 400);
    return () => clearTimeout(t);
  }, [idType, ident]);
  return state;
}

function IdNotice({ kind, idType, check }) {
  let tone = "text-muted-foreground", msg = "";
  if (kind === "informal") {
    if (check.status === "found") { tone = "text-destructive"; msg = `Inscrita en la DGII como ${check.reg.razon_social}: es contribuyente, pídele su comprobante (compra formal).`; }
    else if (check.status === "notfound") { tone = "text-success"; msg = "No aparece en el padrón DGII: correcto para E41."; }
    else msg = "La cédula es obligatoria en el E41, tanto en bienes como en servicios.";
  } else {
    if (check.status === "found") { tone = "text-success"; msg = `DGII: ${check.reg.razon_social} · ${check.reg.estado}`; }
    else if (check.status === "notfound") { tone = "text-amber-700"; msg = "No aparece en el padrón DGII: verifica el número del comprobante."; }
  }
  return msg ? <p className={`text-[11px] ${tone}`} data-testid="purchase-occ-id-notice">{msg}</p> : null;
}
